import React, { useState } from 'react';
import currency from 'helpers/currency';
import { Eye, EyeOff, Activity, DollarSign, Clock, ArrowDownCircle } from 'react-feather';
import styled from 'styled-components';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';

export default function StatisticsCards({ data }) {
    const [isTotalVisible, setIsTotalVisible] = useState(false);
    const [isPendingVisible, setIsPendingVisible] = useState(false);
    const [isAvailableVisible, setIsAvailableVisible] = useState(false);

    return (
        <Container>
            <Card accentColor={colors.primary}>
                <CardTop>
                    <IconBox bgColor={`${colors.primary}14`}>
                        <Activity size={18} color={colors.primary} />
                    </IconBox>
                    <Label>Transações efetuadas</Label>
                </CardTop>
                <Value color={colors.primaryDark}>
                    {data?.count_total_transacted || 0}
                </Value>
                <Subtext>Transações processadas</Subtext>
            </Card>

            <Card accentColor={colors.gold}>
                <CardTop>
                    <IconBox bgColor={`${colors.gold}14`}>
                        <DollarSign size={18} color={colors.gold} />
                    </IconBox>
                    <Label>Total transacionado</Label>
                </CardTop>
                <ValueRow>
                    <Value color={colors.goldDark}>
                        {isTotalVisible ? currency.format(data?.total_transactions_carried_out) : 'R$ *****'}
                    </Value>
                    <Toggle
                        onClick={() => setIsTotalVisible(v => !v)}
                        aria-label="Alternar visibilidade do total transacionado"
                    >
                        {isTotalVisible ? <EyeOff size={16} /> : <Eye size={16} />}
                    </Toggle>
                </ValueRow>
            </Card>

            <Card accentColor={colors.occasionalPurple}>
                <CardTop>
                    <IconBox bgColor={`${colors.occasionalPurple}12`}>
                        <Clock size={18} color={colors.occasionalPurple} />
                    </IconBox>
                    <Label>Total a receber</Label>
                </CardTop>
                <ValueRow>
                    <Value color={colors.secondaryPurple}>
                        {isPendingVisible ? currency.format(data?.balance_pending) : 'R$ *****'}
                    </Value>
                    <Toggle
                        onClick={() => setIsPendingVisible(v => !v)}
                        aria-label="Alternar visibilidade do total a receber"
                    >
                        {isPendingVisible ? <EyeOff size={16} /> : <Eye size={16} />}
                    </Toggle>
                </ValueRow>
                <Subtext>Liberação pendente</Subtext>
            </Card>

            <Card accentColor={colors.success}>
                <CardTop>
                    <IconBox bgColor={`${colors.success}15`}>
                        <ArrowDownCircle size={18} color={colors.success} />
                    </IconBox>
                    <Label>Disponível para saque</Label>
                </CardTop>
                <ValueRow>
                    <Value color={colors.successDark}>
                        {isAvailableVisible ? currency.format(data?.balance_available) : 'R$ *****'}
                    </Value>
                    <Toggle
                        onClick={() => setIsAvailableVisible(v => !v)}
                        aria-label="Alternar visibilidade do disponível para saque"
                    >
                        {isAvailableVisible ? <EyeOff size={16} /> : <Eye size={16} />}
                    </Toggle>
                </ValueRow>
                <Subtext>Pronto para sacar</Subtext>
            </Card>
        </Container>
    );
}

const Container = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 10px 0;

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        grid-template-columns: repeat(4, 1fr);
    }
`;

const Card = styled.div`
    background: ${colors.neutralLight};
    border-radius: 12px;
    padding: 20px;
    border-left: 3px solid ${({ accentColor }) => accentColor};
    box-shadow: 0 1px 3px ${colors.night}0A;
    transition: box-shadow 0.2s ease, transform 0.2s ease;

    &:hover {
        box-shadow: 0 4px 12px ${colors.night}12;
        transform: translateY(-1px);
    }
`;

const CardTop = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;
`;

const IconBox = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: ${({ bgColor }) => bgColor};
    flex-shrink: 0;
`;

const Label = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.night};
    line-height: 1.3;
`;

const Value = styled.div`
    font-size: 24px;
    font-weight: 700;
    color: ${({ color }) => color || colors.nightDark};
    letter-spacing: -0.5px;
    line-height: 1.2;
`;

const ValueRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const Toggle = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    background: ${colors.night}0A;
    border-radius: 8px;
    cursor: pointer;
    color: ${colors.night};
    transition: background 0.15s ease;
    flex-shrink: 0;

    &:hover {
        background: ${colors.night}18;
    }

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 2px;
    }
`;

const Subtext = styled.span`
    display: block;
    font-size: 12px;
    color: ${colors.nightLight};
    margin-top: 8px;
`;
